import { allOf, answerEquals, formResult, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * Owner intake: call an owner who asked about property management and take
 * eight questions, one of each answer type, in a form. The
 * owner can step off the questions at any point: the agent answers from the
 * knowledge the company attaches, then goes back to the question. A primary home
 * routes to a short "we manage rentals" talk; every other owner gets the
 * proposal wrap-up. A request for a person, a lead who is not interested, and
 * an opt-out end the call with a note for the team.
 *
 * The template carries no facts. Attach the company's knowledge bases to the
 * graph or the form; with none, the agent says the sales team will follow up.
 */
const START_ID = '00000000-0000-4000-8000-00000008d001';
const GREET_ID = '00000000-0000-4000-8000-00000008a001';
const INTAKE_ID = '00000000-0000-4000-8000-00000008f001';
const PRIMARY_HOME_ID = '00000000-0000-4000-8000-00000008c001';
const WRAP_UP_ID = '00000000-0000-4000-8000-00000008c002';
const END_SIGN_OFF_ID = '00000000-0000-4000-8000-00000008a002';
const END_HANDOFF_ID = '00000000-0000-4000-8000-00000008a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000008a004';
const END_NOT_A_FIT_ID = '00000000-0000-4000-8000-00000008a005';
const edgeId = (n) => `00000000-0000-4000-8000-00000008e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(11),
    toNodeId: GREET_ID,
});
const PERSONA = [
    'You are Sam, an automated assistant for Super, a property management',
    'company. You call owners who asked about property management. Be warm and',
    'brief: one or two short spoken sentences per turn.',
    '',
    'Answer the owner only from your knowledge. When it does not cover a',
    'question, say the sales team will answer it in the proposal.',
].join('\n');
export const OWNER_INTAKE_GRAPH_TEMPLATE = {
    name: 'Owner intake',
    persona: PERSONA,
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.NOT_INTERESTED,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: "Hi, this is Sam with Super, calling about the property management request you sent in. I'm an automated assistant, and I'll pass your answers to our sales team.",
        },
        {
            id: INTAKE_ID,
            type: 'form',
            title: 'Owner intake',
            position: 2,
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            items: [
                {
                    kind: 'prompt',
                    id: 'intake_intro',
                    text: 'I have a few quick questions so the team can prepare.',
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'owner_name',
                    text: 'Who should our team ask for when they follow up?',
                    answerType: { kind: 'string' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'property_zip',
                    text: "What's the zip code of the property?",
                    answerType: { kind: 'zip' },
                    required: true,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'unit_count',
                    text: 'How many units does the property have?',
                    answerType: { kind: 'integer', min: 1, max: 500 },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'property_situation',
                    text: 'Is the property rented right now, vacant, or your primary home?',
                    answerType: {
                        kind: 'options',
                        options: ['rented', 'vacant', 'primary'],
                    },
                    required: true,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'has_manager',
                    text: 'Do you have a property manager today?',
                    answerType: { kind: 'yes_no' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'start_timeline',
                    text: 'Ask when they would like new management to start, and what is driving the timing.',
                    answerType: { kind: 'free_text' },
                    required: false,
                    mode: 'instruction',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'target_date',
                    text: 'If you have a target date for us to take over, what is it?',
                    answerType: { kind: 'date' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'contact_email',
                    text: "What's the best email to send the proposal to?",
                    answerType: { kind: 'email' },
                    required: true,
                    mode: 'verbatim',
                    depth: 0,
                },
            ],
        },
        {
            id: PRIMARY_HOME_ID,
            type: 'conversation',
            title: 'Primary home',
            position: 3,
            prompt: 'The owner lives in the property. Explain that we manage rental homes, ' +
                'so we take on a primary home only when the owner plans to rent it ' +
                'out. Ask if they plan to rent it out soon.',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'plans_to_rent',
                    text: 'The owner plans to rent the property out soon',
                },
                {
                    id: 'not_renting',
                    text: 'The owner does not plan to rent the property out soon',
                },
            ],
        },
        {
            id: WRAP_UP_ID,
            type: 'conversation',
            title: 'Wrap up',
            position: 4,
            prompt: 'Confirm that the sales team will email a written proposal within one ' +
                'business day. Then ask if there is anything else the team should know.',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'no_more_details',
                    text: 'The owner said they have nothing else for the sales team',
                },
            ],
        },
        {
            id: END_SIGN_OFF_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: 'Thanks for your time. Watch your inbox for the proposal. Have a great day!',
            },
        },
        {
            id: END_HANDOFF_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 6,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll make a note for the team so they know. Have a great day.",
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 7,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls from us. Have a good day.",
            },
        },
        {
            id: END_NOT_A_FIT_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 8,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: 'No problem. If you decide to rent it out, give us a call. Have a great day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(2),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_HANDOFF_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: END_HANDOFF_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.NOT_INTERESTED },
            position: 3,
        },
        { ...OPENING.edges[1], position: 4 },
        {
            id: edgeId(5),
            fromNodeId: GREET_ID,
            toNodeId: INTAKE_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 5,
        },
        // A primary home first; every other finished form goes to the wrap-up.
        {
            id: edgeId(6),
            fromNodeId: INTAKE_ID,
            toNodeId: PRIMARY_HOME_ID,
            condition: allOf(formResult(INTAKE_ID, 'completed'), answerEquals(INTAKE_ID, 'property_situation', 'primary')),
            position: 6,
        },
        {
            id: edgeId(7),
            fromNodeId: INTAKE_ID,
            toNodeId: WRAP_UP_ID,
            condition: allOf(formResult(INTAKE_ID, 'completed')),
            position: 7,
        },
        {
            id: edgeId(8),
            fromNodeId: PRIMARY_HOME_ID,
            toNodeId: WRAP_UP_ID,
            condition: allOf(signalEquals(PRIMARY_HOME_ID, 'plans_to_rent', true)),
            position: 8,
        },
        {
            id: edgeId(9),
            fromNodeId: PRIMARY_HOME_ID,
            toNodeId: END_NOT_A_FIT_ID,
            condition: allOf(signalEquals(PRIMARY_HOME_ID, 'not_renting', true)),
            position: 9,
        },
        {
            id: edgeId(10),
            fromNodeId: WRAP_UP_ID,
            toNodeId: END_SIGN_OFF_ID,
            condition: allOf(signalEquals(WRAP_UP_ID, 'no_more_details', true)),
            position: 10,
        },
    ],
};
//# sourceMappingURL=owner-intake-template.js.map